/**
 * Görsel preset çözümleme — asıl kaynak admin panel (`/admin/settings`).
 * Aşağıdaki DEFAULT_* yalnızca panel/SSR ayarı yoksa fallback’tir.
 */

import type { ImagePreset } from '@/types/media';
import { parsePresetToOptions, type PresetOptions } from './parsePresetToOptions';

/** Kod tarafında kullanılan anahtarlar (admin preset.key ile eşleşmeli) */
export const IMAGE_PRESETS = {
  masthead: 'masthead',
  mastheadGrid: 'mastheadGrid',
  sectionLead: 'sectionLead',
  sideCard: 'sideCard',
  listCard: 'listCard',
  card: 'card',
  /** Ana sayfa Shorts raftı — dikey küçük thumb */
  shortsThumb: 'shortsThumb',
  breakingThumb: 'breakingThumb',
  thumb: 'thumb',
  thumbSm: 'thumbSm',
  /** Profil / yazar avatarı — kare crop */
  avatar: 'avatar',
  detail: 'detail',
  videoPoster: 'videoPoster',
  gallery: 'gallery',
  og: 'og',
  /** Etiket bilgi paneli — kırpmasız, oran korunur */
  tagInfo: 'tagInfo',
} as const;

export type ImagePresetKey = keyof typeof IMAGE_PRESETS;

/** WebP kalite tavanı — tüm görseller maksimum kalite */
const DELIVERY_QUALITY_CAP = 100

/** Panel yoksa / boş kayıt fallback — boyutlar aynı, kalite maksimum (100) */
export const DEFAULT_IMAGE_PRESET_OPTIONS: Record<string, PresetOptions> = {
  // Manşet: 720 taban; srcset/retina max 960 (1260 kullanılmaz)
  masthead: { width: 720, height: 400, quality: 100, format: 'webp', fit: 'cover' },
  mastheadGrid: { width: 720, height: 400, quality: 100, format: 'webp', fit: 'cover' },
  sectionLead: { width: 720, height: 400, quality: 100, format: 'webp', fit: 'cover' },
  sideCard: { width: 320, height: 200, quality: 100, format: 'webp', fit: 'cover' },
  listCard: { width: 400, height: 192, quality: 100, format: 'webp', fit: 'cover' },
  card: { width: 640, height: 480, quality: 100, format: 'webp', fit: 'cover' },
  shortsThumb: { width: 240, height: 420, quality: 100, format: 'webp', fit: 'cover' },
  breakingThumb: { width: 176, height: 112, quality: 100, format: 'webp', fit: 'cover' },
  thumb: { width: 200, height: 133, quality: 100, format: 'webp', fit: 'cover' },
  thumbSm: { width: 160, height: 120, quality: 100, format: 'webp', fit: 'cover' },
  avatar: { width: 200, height: 200, quality: 100, format: 'webp', fit: 'cover' },
  detail: { width: 960, height: 540, quality: 100, format: 'webp', fit: 'cover' },
  videoPoster: { width: 960, height: 533, quality: 100, format: 'webp', fit: 'cover' },
  gallery: { width: 960, height: 540, quality: 100, format: 'webp', fit: 'inside' },
  og: { width: 1200, height: 675, quality: 100, format: 'webp', fit: 'cover' },
  tagInfo: { width: 400, height: 320, quality: 100, format: 'webp', fit: 'inside' },
  // Eski admin anahtarları
  headline: { width: 960, height: 533, quality: 100, format: 'webp', fit: 'cover' },
  thumbnail: { width: 200, height: 133, quality: 100, format: 'webp', fit: 'cover' },
};

/** CDN teslimat tavanı — admin/retina ne derse desin aşılmaz */
const DELIVERY_SIZE_CAPS: Record<string, { width: number; height: number }> = {
  masthead: { width: 960, height: 533 },
  headline: { width: 960, height: 533 },
  videoPoster: { width: 960, height: 533 },
  mastheadGrid: { width: 720, height: 400 },
  sectionLead: { width: 720, height: 400 },
  sideCard: { width: 640, height: 400 },
  listCard: { width: 800, height: 384 },
  card: { width: 720, height: 540 },
  shortsThumb: { width: 240, height: 420 },
  breakingThumb: { width: 352, height: 224 },
  thumb: { width: 200, height: 133 },
  thumbSm: { width: 160, height: 120 },
  avatar: { width: 400, height: 400 },
  detail: { width: 960, height: 540 },
  gallery: { width: 960, height: 540 },
  tagInfo: { width: 400, height: 320 },
};

function clampDeliverySize(key: string, options: PresetOptions): PresetOptions {
  const cap = DELIVERY_SIZE_CAPS[key]
  if (!cap || !options.width || options.width <= cap.width) return options
  const ratio = cap.width / options.width
  return {
    ...options,
    width: cap.width,
    height: options.height
      ? Math.round(options.height * ratio)
      : cap.height,
  }
}

function capDeliveryQuality(key: string, options: PresetOptions): PresetOptions {
  if (key === 'og' || !options.quality) return options
  if (options.quality <= DELIVERY_QUALITY_CAP) return options
  return { ...options, quality: DELIVERY_QUALITY_CAP }
}

const KEY_ALIASES: Record<string, string> = {
  headline: 'masthead',
  thumbnail: 'thumb',
};

function adminPresetToOptions(preset: ImagePreset): PresetOptions {
  const format =
    !preset.format || preset.format === 'original'
      ? 'webp'
      : (preset.format as PresetOptions['format']);
  return {
    width: preset.width,
    height: preset.height,
    quality: preset.quality,
    format,
    fit: preset.fit || 'cover',
  };
}

function isMastheadPresetKey(key: string, aliasTarget?: string): boolean {
  return (
    key === 'masthead' ||
    key === 'headline' ||
    key === 'videoPoster' ||
    key === 'mastheadGrid' ||
    key === 'sectionLead' ||
    aliasTarget === 'masthead'
  );
}

/** Ana sayfa ortası ~630–720px CSS — 1260 byte israfı; retina tavanı 960 */
const MASTHEAD_DELIVERY_MAX_W = 960
const MASTHEAD_DELIVERY_MAX_H = 533

/**
 * Manşet: admin 630×350 ise büyüt ama 960×533’ü aşma (eski 2× → 1260 yasak).
 * Diğer preset’ler: retina=true ise 2×.
 */
function applyRetinaDelivery(
  key: string,
  aliasTarget: string | undefined,
  found: ImagePreset | undefined,
  options: PresetOptions,
): PresetOptions {
  if (!options.width && !options.height) return options;

  const explicitOff = found?.retina === false;
  const explicitOn = found?.retina === true;
  const masthead = isMastheadPresetKey(key, aliasTarget);
  if (explicitOff) return options;

  if (masthead) {
    const w = options.width || 720;
    const h = options.height || 400;
    if (w >= MASTHEAD_DELIVERY_MAX_W) {
      return {
        ...options,
        width: MASTHEAD_DELIVERY_MAX_W,
        height: MASTHEAD_DELIVERY_MAX_H,
      };
    }
    const scale = Math.min(2, MASTHEAD_DELIVERY_MAX_W / w);
    return {
      ...options,
      width: Math.round(w * scale),
      height: Math.round(h * scale),
    };
  }

  if (!explicitOn) return options;

  const w = options.width || 0;
  if (w >= 960) return options;

  return {
    ...options,
    width: w > 0 ? w * 2 : options.width,
    height: options.height && options.height > 0 ? options.height * 2 : options.height,
  };
}

function getRuntimePresets(): ImagePreset[] {
  if (typeof window !== 'undefined') {
    const cached = (window as unknown as { __mediaSettingsCache?: { presets?: ImagePreset[] } })
      .__mediaSettingsCache;
    if (Array.isArray(cached?.presets) && cached.presets.length > 0) {
      return cached.presets;
    }
    try {
      const raw = localStorage.getItem('__mediaSettings');
      if (raw) {
        const parsed = JSON.parse(raw) as { presets?: ImagePreset[] };
        if (Array.isArray(parsed?.presets)) return parsed.presets;
      }
    } catch {
      /* ignore */
    }
  }
  return [];
}

/**
 * @param size - `masthead` | `rw1260h700q85gc` | undefined
 * @param presets - SSR’de getMediaSettingsServer().presets geçir (panel değerleri)
 * @param opts.retina - false: admin ham boyut (Polotno canvas); default true: CDN teslimatı
 */
export function resolveImagePreset(
  size?: string,
  presets?: ImagePreset[] | null,
  opts?: { retina?: boolean },
): PresetOptions {
  if (!size || !size.trim()) return {};

  const trimmed = size.trim();

  // Legacy compact string: rw1260h700q85gc
  if (/^r?w\d+/i.test(trimmed) || /w\d+h\d+/i.test(trimmed)) {
    return parsePresetToOptions(trimmed);
  }

  const list = presets && presets.length > 0 ? presets : getRuntimePresets();
  const aliasTarget = KEY_ALIASES[trimmed];

  const found =
    list.find((p) => p.enabled !== false && p.key === trimmed) ||
    (aliasTarget
      ? list.find((p) => p.enabled !== false && p.key === aliasTarget)
      : undefined) ||
    list.find((p) => p.enabled !== false && KEY_ALIASES[p.key] === trimmed);

  // Panel kaydı varsa o kullanılır; yoksa DEFAULT_* fallback
  const base = found
    ? adminPresetToOptions(found)
    : DEFAULT_IMAGE_PRESET_OPTIONS[trimmed] ||
      (aliasTarget ? DEFAULT_IMAGE_PRESET_OPTIONS[aliasTarget] : undefined) ||
      {};

  if (opts?.retina === false) {
    return clampDeliverySize(
      trimmed,
      capDeliveryQuality(trimmed, base),
    )
  }

  return clampDeliverySize(
    trimmed,
    capDeliveryQuality(
      trimmed,
      applyRetinaDelivery(trimmed, aliasTarget, found, base),
    ),
  )
}

/**
 * Admin masthead preset boyutu (retina yok) — UI / Polotno oran referansı.
 */
export function getMastheadCanvasSize(): { width: number; height: number } {
  const opts = resolveImagePreset(IMAGE_PRESETS.masthead, null, { retina: false });
  const fallback = DEFAULT_IMAGE_PRESET_OPTIONS.masthead;
  return {
    width: Math.max(100, opts.width || fallback.width || 720),
    height: Math.max(100, opts.height || fallback.height || 400),
  };
}

/**
 * Polotno PNG master: preset’in 2×’i (retina master).
 * CDN de 2× sunar → ekranda keskin.
 */
export function getMastheadExportSize(): { width: number; height: number } {
  const { width, height } = getMastheadCanvasSize();
  return {
    width: Math.max(width, width * 2),
    height: Math.max(height, height * 2),
  };
}

/** Admin UI / backend seed için tam preset listesi */
export function buildDefaultAdminPresets(): ImagePreset[] {
  return (Object.keys(DEFAULT_IMAGE_PRESET_OPTIONS) as string[])
    .filter((key) => !['headline', 'thumbnail'].includes(key))
    .map((key) => {
      const o = DEFAULT_IMAGE_PRESET_OPTIONS[key];
      return {
        key,
        width: o.width || 800,
        height: o.height,
        fit: (o.fit || 'cover') as ImagePreset['fit'],
        format: (o.format || 'webp') as ImagePreset['format'],
        quality: o.quality ?? 100,
        progressive: true,
        allowUpscale: false,
        retina: true,
        enabled: true,
      };
    });
}
